import { AssessmentOutlined, ContentCopyOutlined } from '@mui/icons-material';
import { Box, Skeleton, styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { ReportType } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useGetRecentReports } from '@/hooks/api/reports/useGetRecentReports';
import { useTenants } from '@/services/tenants/useTenants';
import { isReportInProgress } from '@/utils/reportStatus';
import { DashboardTile } from './DashboardTile';
import { DuplicationSummarySection } from './DuplicationSummarySection';
import { RecentReportRow } from './RecentReportRow';
import { ReportsTileHeader } from './ReportsTileHeader';
import { TileEmptyState } from './TileEmptyState';

const RowList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const ViewAllLink = styled(Link)(({ theme }) => ({
  flexShrink: 0,
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.secondary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const ReportsTile = () => {
  const { getPath } = useTenants();
  const { data: reports, isLoading, isError } = useGetRecentReports();

  const rows = reports ?? [];
  const isDuplicationReportRunning = rows.some(
    report => report.reportType === ReportType.DUPLICATION && isReportInProgress(report.status)
  );

  return (
    <DashboardTile
      id='reports'
      title='Reports'
      icon={<AssessmentOutlined />}
      action={<ViewAllLink to={getPath('report')}>View all</ViewAllLink>}
    >
      {isError && <BodyText color='text.secondary'>Failed to load recent reports.</BodyText>}
      {!isError && isLoading && <Skeleton variant='rounded' height={120} />}
      {/* One empty state, not two: an empty tenant has no duplication report either, and stacking
          "No reports yet" above "No duplication report yet" says the same thing twice. */}
      {!isError && !isLoading && rows.length === 0 && (
        <TileEmptyState
          testId='no-recent-reports'
          graphic={<ContentCopyOutlined />}
          title='No reports yet'
          description='A duplication report finds identical parts in your catalog. Recent reports show here.'
          action={{ label: 'Run a duplication report', to: getPath('report') }}
        />
      )}
      {!isError && !isLoading && rows.length > 0 && (
        <>
          <RowList>
            <ReportsTileHeader />
            {rows.map(report => (
              <RecentReportRow key={report.id} report={report} />
            ))}
          </RowList>
          <DuplicationSummarySection
            pollWhileRunning={isDuplicationReportRunning}
            listedReportIds={rows.map(report => report.id)}
          />
        </>
      )}
    </DashboardTile>
  );
};
